import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  useToast,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  Select,
  FormControl,
  FormLabel,
  Collapse,
  IconButton,
  Code,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from "@chakra-ui/icons";
import { getSupabase, getSupabaseAdmin } from "../components/supabaseClient";
import {
  documentBackupBucketName,
  getDailyVersionLimit,
  getEndOfDayVersionLimit,
  userRoles,
  isUserBasedVersioningEnabled,
  getMaxUsersForLimit,
  getSingleUserExtraVersions,
  supabaseServiceKey,
  getBackendURL,
} from "../components/env";
import { supabaseSchema } from "../components/supabaseClient";
import DiffModal from "./DiffModal";

const VersionHistoryModal = ({
  isOpen,
  onClose,
  documentPath,
  onVersionRestored,
  userRole = "USERS",
  appointmentVersionInfo = null,
  versioningSystem = null,
  /** When true, Restore Version is disabled (e.g. tenant viewer read-only) */
  documentReadOnly = false,
}) => {
  // Cross-schema: a FOREIGN patient's versions live in the PATIENT's schema
  // (carried on the VersioningSystem instance this modal is handed), not the
  // viewer's home. null → home (EditableDocumentPage / same-schema) — unchanged.
  const modalSchema = versioningSystem?.schema || null;
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedUser, setSelectedUser] = useState("everyone");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [diffVersions, setDiffVersions] = useState({ from: null, to: null });
  const [showAllVersionsForDate, setShowAllVersionsForDate] = useState(
    new Set()
  );
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [diffModalData, setDiffModalData] = useState(null);
  const [isReducing, setIsReducing] = useState(false);
  const [showReduceTooltip, setShowReduceTooltip] = useState(false);
  const toast = useToast();

  // Load versions when modal opens
  useEffect(() => {
    if (isOpen && documentPath) {
      loadVersions();
    }
  }, [isOpen, documentPath]);

  // Populate version_name for versions that don't have it
  // Also renumber auto versions to ensure consistency (oldest = Version 1, newest = highest number)
  const populateVersionNames = async (versionsData, supabaseAdmin) => {
    // Versions are already sorted by backup_timestamp descending (most recent first)
    // Version numbers are ascending from oldest to newest
    // Index 0 = newest (highest number), Index N-1 = oldest (Version 1)
    const versionsToUpdate = [];
    const totalVersions = versionsData.length;
    
    for (let i = 0; i < versionsData.length; i++) {
      const version = versionsData[i];
      // Oldest version (last index) = Version 1, newest version (first index) = Version N
      const versionNumber = totalVersions - i;
      
      // For auto versions: always renumber based on position to ensure consistency
      // For manual versions: only update if they don't have a name (preserve custom names)
      if (version.backup_type === 'auto') {
        const calculatedName = `Version ${versionNumber}`;
        // Only update if the name doesn't match the expected name
        if (version.version_name !== calculatedName) {
          versionsToUpdate.push({
            id: version.id,
            version_name: calculatedName,
          });
          // Update the version object in place
          version.version_name = calculatedName;
        }
      } else if (!version.version_name) {
        // Manual version without a name - assign based on position
        const calculatedName = `Version ${versionNumber}`;
        versionsToUpdate.push({
          id: version.id,
          version_name: calculatedName,
        });
        // Update the version object in place
        version.version_name = calculatedName;
      }
      // Manual versions with existing version_name are preserved (custom names)
    }
    
    // Batch update versions in database if there are any to update
    if (versionsToUpdate.length > 0) {
      try {
        // Update each version individually (Supabase doesn't support batch updates easily)
        await Promise.all(
          versionsToUpdate.map(async (v) => {
            try {
              const { error } = await supabaseAdmin
                .from("document_backups")
                .update({ version_name: v.version_name })
                .eq("id", v.id);
              
              if (error) {
                console.warn(`Failed to update version_name for version ${v.id}:`, error);
              }
            } catch (err) {
              console.warn(`Error updating version_name for version ${v.id}:`, err);
            }
          })
        );
        
        console.log(`✅ Updated version_name for ${versionsToUpdate.length} versions`);
      } catch (error) {
        console.warn("Error updating version names in database:", error);
        // Continue anyway - we've updated the local data
      }
    }
    
    return versionsData;
  };

  const handleReduceOldVersions = async () => {
    if (!documentPath) return;
    setIsReducing(true);
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/versioning/thin-document/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_path: documentPath, schema: modalSchema || supabaseSchema }),
      });
      if (!res.ok) {
        throw new Error(`Server error ${res.status}`);
      }
      const result = await res.json();
      const { days_checked, days_reduced, versions_deleted } = result;
      if (versions_deleted === 0) {
        toast({
          title: "Nothing to reduce",
          description: `Checked ${days_checked} past day(s) — all already have ${3} or fewer auto versions.`,
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Old versions reduced",
          description: `Removed ${versions_deleted} version(s) across ${days_reduced} day(s).`,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        // Invalidate the cache so the reload below fetches fresh data from DB.
        versioningSystem?.invalidateVersionsCache();
        loadVersions();
      }
    } catch (e) {
      toast({
        title: "Failed to reduce versions",
        description: e?.message || "An error occurred.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsReducing(false);
    }
  };

  const loadVersions = async () => {
    setLoading(true);
    setError(null);

    // Declare versionsData at the top
    let versionsData = [];

    try {
      // Use the in-memory versions cache when available — avoids a DB round-trip
      // on every modal open after the first.
      const cachedVersions = versioningSystem?.getVersionsCache();
      if (cachedVersions !== null && cachedVersions !== undefined) {
        // Objects are shared with the cache array so any in-place mutations
        // (e.g. version.version_name = ...) stay reflected in the cache.
        versionsData = [...cachedVersions];
      } else {
        // Guard: Check if Supabase config is fully loaded before proceeding
        if (!supabaseServiceKey) {
          setError("Configuration not fully loaded. Please refresh the page.");
          setLoading(false);
          return;
        }

        // Read versions from the DOC's schema (the patient's, for a cross-schema
        // chart). Storage + users lookups below stay on the raw admin — the
        // backup bucket is global and the schema is already in the backup path.
        const supabaseAdmin = getSupabaseAdmin();
        const backupsDb = modalSchema ? supabaseAdmin.schema(modalSchema) : supabaseAdmin;

        const { data, error } = await backupsDb
          .from("document_backups")
          .select("*")
          .eq("original_path", documentPath)
          .order("backup_timestamp", { ascending: false });

        if (error) {
          console.error("Error fetching versions:", error);
          throw error;
        } else {
          versionsData = data || [];
        }

        // Check for versions with missing user_name and try to fetch from backup content or users table
        const versionsWithMissingUsers = versionsData.filter((v) => !v.user_name);
        if (versionsWithMissingUsers.length > 0) {
          console.log(
            `Found ${versionsWithMissingUsers.length} versions with missing user information, attempting to fetch...`
          );

          // First, try to fetch user information from backup content (cheap and avoids extra DB hits)
          const updatedFromBackups = await Promise.all(
            versionsWithMissingUsers.map(async (version) => {
              try {
                const { data: backupData, error: backupError } =
                  await supabaseAdmin.storage
                    .from(documentBackupBucketName)
                    .download(version.backup_path);
                if (backupError) return version;
                const content = await backupData.text();
                const parsedContent = JSON.parse(content);
                const userInfo = extractUserInfoFromBackup(parsedContent);
                return userInfo ? { ...version, user_name: userInfo } : version;
              } catch (_e) {
                return version;
              }
            })
          );

          // Merge backup-derived names
          versionsData = versionsData.map(
            (v) => updatedFromBackups.find((u) => u.id === v.id) || v
          );

          // Next, for any still-missing names, try resolving via users table using user_id or user_email
          const stillMissing = versionsData.filter(
            (v) => !v.user_name && (v.user_id || v.user_email)
          );
          if (stillMissing.length > 0) {
            const ids = [
              ...new Set(stillMissing.map((v) => v.user_id).filter(Boolean)),
            ];
            const emails = [
              ...new Set(stillMissing.map((v) => v.user_email).filter(Boolean)),
            ];

            let idNameMap = new Map();
            let emailNameMap = new Map();
            try {
              if (ids.length > 0) {
                const { data: byIds, error: idErr } = await supabaseAdmin
                  .from("users")
                  .select("id,name,email")
                  .in("id", ids);
                if (!idErr && Array.isArray(byIds)) {
                  byIds.forEach((u) => idNameMap.set(u.id, u.name || u.email));
                }
              }
            } catch (e) {
              console.warn("User lookup by id failed:", e);
            }
            try {
              if (emails.length > 0) {
                const { data: byEmails, error: emErr } = await supabaseAdmin
                  .from("users")
                  .select("id,name,email")
                  .in("email", emails);
                if (!emErr && Array.isArray(byEmails)) {
                  byEmails.forEach((u) =>
                    emailNameMap.set(u.email, u.name || u.email)
                  );
                }
              }
            } catch (e) {
              console.warn("User lookup by email failed:", e);
            }

            versionsData = versionsData.map((v) => {
              if (v.user_name) return v;
              const resolvedName =
                (v.user_id && idNameMap.get(v.user_id)) ||
                (v.user_email && emailNameMap.get(v.user_email));
              if (resolvedName) {
                return { ...v, user_name: resolvedName };
              }
              // Fallback: readable placeholder to avoid collapsing all under "Unknown"
              const fallback =
                v.user_email ||
                (v.user_id ? `User ${String(v.user_id).slice(0, 8)}` : "Unknown");
              return { ...v, user_name: fallback };
            });
          }
        }

        // Populate version_name for versions that don't have it and persist to DB.
        // Only runs on a fresh DB load — cached data already has names resolved.
        versionsData = await populateVersionNames(versionsData, backupsDb);

        // Populate the cache so subsequent modal opens skip the DB round-trip.
        versioningSystem?.updateVersionsCache(versionsData);
      }

      setVersions(versionsData);

      // Extract unique users from versions
      const users = [
        ...new Set(versionsData.map((v) => v.user_name).filter(Boolean)),
      ];
      setAvailableUsers(users);

    } catch (err) {
      console.error("Error loading versions:", err);
      setError("Failed to load version history");
    } finally {
      setLoading(false);
    }
  };

  const loadVersionContent = async (version) => {
    setLoadingPreview(true);
    setSelectedVersion(version);

    try {
      // Check the content cache first — avoids a storage download on every click.
      const cached = versioningSystem?.getCachedContent(version.backup_path);
      if (cached) {
        setPreviewContent(cached);
        setLoadingPreview(false);
        return;
      }

      // Guard: Check if Supabase config is fully loaded before proceeding
      if (!supabaseServiceKey) {
        throw new Error("Configuration not fully loaded. Please refresh the page.");
      }

      // Use current schema admin client to access storage
      const supabaseAdmin = getSupabaseAdmin();
      const { data, error } = await supabaseAdmin.storage
        .from(documentBackupBucketName)
        .download(version.backup_path);

      if (error) {
        throw error;
      }

      const content = await data.text();
      const parsedContent = JSON.parse(content);

      // Store in content cache for future previews / diffs.
      versioningSystem?.setCachedContent(version.backup_path, parsedContent);

      setPreviewContent(parsedContent);
    } catch (err) {
      console.error("Error loading version content:", err);
      toast({
        title: "Error",
        description: "Failed to load version content",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const restoreVersion = async () => {
    if (!selectedVersion || !onVersionRestored) return;

    try {
      if (previewContent && previewContent.delta) {
        await onVersionRestored(previewContent.delta);
        toast({
          title: "Version Restored",
          description: "Document has been restored to the selected version",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
      } else {
        throw new Error("No valid content to restore");
      }
    } catch (err) {
      console.error("Error restoring version:", err);
      toast({
        title: "Restore Failed",
        description: "Failed to restore the selected version",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getVersionTypeColor = (type) => {
    switch (type) {
      case "auto":
        return "blue";
      case "manual":
        return "green";
      default:
        return "gray";
    }
  };

  // Extract text content from Quill delta for preview
  const extractTextFromDelta = (delta) => {
    if (!delta || !delta.ops) return "";

    return delta.ops
      .filter((op) => op.insert && typeof op.insert === "string")
      .map((op) => op.insert)
      .join("")
      .trim();
  };

  // Get a preview snippet of the content
  const getContentPreview = (content) => {
    if (!content) return "No content available";

    let text = "";
    if (content.delta) {
      text = extractTextFromDelta(content.delta);
    } else if (content.content) {
      text = extractTextFromDelta(content.content);
    }

    if (!text) return "No text content found";

    // Limit preview to first 300 characters
    const maxLength = 300;
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }

    return text;
  };

  // Filter versions based on selected user
  const getFilteredVersions = () => {
    let filtered;
    if (selectedUser === "everyone") {
      filtered = versions;
    } else {
      filtered = versions.filter(
        (version) => version.user_name === selectedUser
      );
    }

    return filtered;
  };

  const filteredVersions = getFilteredVersions();

  // Get the original version number for a version (based on all versions, not filtered)
  const getOriginalVersionNumber = (version) => {
    const originalIndex = versions.findIndex((v) => v.id === version.id);
    return versions.length - originalIndex;
  };

  // Get display name for version (uses version_name if available, otherwise calculates it)
  const getVersionDisplayName = (version) => {
    if (version.version_name) {
      return version.version_name;
    }
    // Fallback to calculated version number
    return `Version ${getOriginalVersionNumber(version)}`;
  };

  // Get version number for sorting (uses calculated version number for all versions)
  const getVersionNumberForSorting = (version) => {
    // Use the original version number calculation which is based on chronological position
    // This ensures custom named versions are positioned correctly relative to numbered versions
    return getOriginalVersionNumber(version);
  };

  // Sort versions by version number (descending: 10, 9, 8...)
  // This positions custom named versions chronologically based on their calculated version number
  const sortVersionsByName = (versions) => {
    return [...versions].sort((a, b) => {
      const aVersionNum = getVersionNumberForSorting(a);
      const bVersionNum = getVersionNumberForSorting(b);
      
      // Sort by version number descending (10, 9, 8...)
      return bVersionNum - aVersionNum;
    });
  };

  // Group versions by date and apply user-based versioning rules
  const getGroupedVersions = () => {
    const filtered = getFilteredVersions();
    const today = new Date().toDateString();

    // Group versions by date
    const groupedByDate = {};
    filtered.forEach((version) => {
      const date = new Date(version.backup_timestamp).toDateString();
      if (!groupedByDate[date]) {
        groupedByDate[date] = [];
      }
      groupedByDate[date].push(version);
    });

    // Process each date with user-based versioning rules
    const processedGroups = {};
    Object.keys(groupedByDate).forEach((date) => {
      const versionsForDate = groupedByDate[date];
      const isToday = date === today;
      const isShowingAllVersions = showAllVersionsForDate.has(date);

      let finalVersions;

      if (isShowingAllVersions) {
        // Show ALL versions for this date (bypass all rules)
        finalVersions = sortVersionsByName(versionsForDate);
      } else if (isUserBasedVersioningEnabled()) {
        // Apply user-based versioning rules
        finalVersions = applyUserBasedVersioningRules(versionsForDate, isToday);
      } else {
        // Fallback to original logic
        const normalLimit = isToday
          ? getDailyVersionLimit()
          : getEndOfDayVersionLimit();
        const sorted = sortVersionsByName(versionsForDate);
        finalVersions = sorted.slice(0, normalLimit);
      }

      processedGroups[date] = finalVersions;
    });

    return processedGroups;
  };

  // Apply user-based versioning rules
  const applyUserBasedVersioningRules = (versionsForDate, isToday) => {
    // Get unique users who edited on this date
    const userVersions = new Map();
    versionsForDate.forEach((version) => {
      const userName = version.user_name || "Unknown User";
      if (!userVersions.has(userName)) {
        userVersions.set(userName, []);
      }
      userVersions.get(userName).push(version);
    });

    const uniqueUsers = Array.from(userVersions.keys());
    const userCount = uniqueUsers.length;

    // Sort all versions by timestamp (most recent first)
    const sortedVersions = versionsForDate.sort(
      (a, b) => new Date(b.backup_timestamp) - new Date(a.backup_timestamp)
    );

    let finalVersions = [];

    if (isToday) {
      // Current day rules
      const dailyLimit = getDailyVersionLimit();
      
      if (userCount <= dailyLimit) {
        // Less than or equal to daily limit users: show most recent from each user + fill to limit with most recent overall
        const userLatestVersions = [];

        // Get most recent version from each user
        uniqueUsers.forEach((userName) => {
          const userVersionsList = userVersions.get(userName);
          const latestVersion = userVersionsList.sort(
            (a, b) =>
              new Date(b.backup_timestamp) - new Date(a.backup_timestamp)
          )[0];
          userLatestVersions.push(latestVersion);
        });

        // Sort user latest versions by timestamp
        userLatestVersions.sort(
          (a, b) => new Date(b.backup_timestamp) - new Date(a.backup_timestamp)
        );

        // Add remaining slots with most recent overall versions
        const remainingSlots = dailyLimit - userLatestVersions.length;
        if (remainingSlots > 0) {
          const remainingVersions = sortedVersions
            .filter((version) => !userLatestVersions.includes(version))
            .slice(0, remainingSlots);
          finalVersions = [...userLatestVersions, ...remainingVersions];
        } else {
          finalVersions = userLatestVersions;
        }
      } else {
        // More users than daily limit: show most recent versions overall (respecting the limit)
        // This ensures we never exceed the daily limit
        finalVersions = sortedVersions.slice(0, dailyLimit);
      }
    } else {
      // End of day rules
      const endOfDayLimit = getEndOfDayVersionLimit();
      
      if (userCount === 1) {
        // Single user: keep most recent + extra versions (up to limit)
        const singleUserVersions = userVersions.get(uniqueUsers[0]);
        const sortedUserVersions = singleUserVersions.sort(
          (a, b) => new Date(b.backup_timestamp) - new Date(a.backup_timestamp)
        );
        const maxVersions = Math.min(
          1 + getSingleUserExtraVersions(),
          endOfDayLimit
        );
        finalVersions = sortedUserVersions.slice(0, maxVersions);
      } else {
        // Multiple users: keep most recent version from each user, but respect the limit
        if (userCount <= endOfDayLimit) {
          // If user count is within limit, show one per user
          uniqueUsers.forEach((userName) => {
            const userVersionsList = userVersions.get(userName);
            const latestVersion = userVersionsList.sort(
              (a, b) =>
                new Date(b.backup_timestamp) - new Date(a.backup_timestamp)
            )[0];
            finalVersions.push(latestVersion);
          });

          // Sort by timestamp
          finalVersions.sort(
            (a, b) => new Date(b.backup_timestamp) - new Date(a.backup_timestamp)
          );
        } else {
          // More users than limit: show most recent versions overall (respecting the limit)
          finalVersions = sortedVersions.slice(0, endOfDayLimit);
        }
      }
    }

    // Sort final versions by version name (descending: 10, 9, 8...) or timestamp for custom names
    return sortVersionsByName(finalVersions);
  };

  // Toggle collapse state for a date
  const toggleDateCollapse = (date) => {
    const newCollapsed = new Set(collapsedDates);
    if (newCollapsed.has(date)) {
      newCollapsed.delete(date);
    } else {
      newCollapsed.add(date);
    }
    setCollapsedDates(newCollapsed);
  };

  // Toggle showing all versions for a specific date
  const toggleShowAllVersions = (date) => {
    const newShowAll = new Set(showAllVersionsForDate);
    if (newShowAll.has(date)) {
      newShowAll.delete(date);
    } else {
      newShowAll.add(date);
    }
    setShowAllVersionsForDate(newShowAll);
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  // Reset selected version when filter changes
  useEffect(() => {
    if (
      selectedVersion &&
      !filteredVersions.find((v) => v.id === selectedVersion.id)
    ) {
      setSelectedVersion(null);
      setPreviewContent(null);
    }
  }, [selectedUser, filteredVersions, selectedVersion]);

  // Extract user information from backup content
  const extractUserInfoFromBackup = (backupContent) => {
    try {
      // Check various possible locations for user information
      if (backupContent.user_name) {
        return backupContent.user_name;
      }

      if (backupContent.userName) {
        return backupContent.userName;
      }

      if (backupContent.user) {
        return backupContent.user;
      }

      if (backupContent.createdBy) {
        return backupContent.createdBy;
      }

      if (backupContent.editedBy) {
        return backupContent.editedBy;
      }

      // Check if there's user information in metadata
      if (backupContent.metadata && backupContent.metadata.user_name) {
        return backupContent.metadata.user_name;
      }

      if (backupContent.metadata && backupContent.metadata.user) {
        return backupContent.metadata.user;
      }

      // Check for user information in version data
      if (backupContent.versionData && backupContent.versionData.user_name) {
        return backupContent.versionData.user_name;
      }

      // Check for user information in the reason field (common in manual versions)
      if (backupContent.reason && typeof backupContent.reason === "string") {
        // Look for patterns like "Manual version: User Name" or "Edited by: User Name."
        const userMatch = backupContent.reason.match(
          /(?:Manual version|Edited by|Created by):\s*(.+)/i
        );
        if (userMatch && userMatch[1]) {
          return userMatch[1].trim();
        }
      }

      // Check for user information in the manualName field
      if (backupContent.manualName) {
        return backupContent.manualName;
      }

      console.log(
        "No user information found in backup content:",
        Object.keys(backupContent)
      );
      return null;
    } catch (error) {
      console.warn("Error extracting user info from backup:", error);
      return null;
    }
  };

  // Check if user has admin privileges
  const isAdmin = () => {
    return userRole === "ADMIN" || userRole === "DOCTOR";
  };

  // Diff functionality
  const handleDiffSelection = (version) => {
    // Check if clicking on an already selected version to unselect it
    const isFromVersion = diffVersions.from?.id === version.id;
    const isToVersion = diffVersions.to?.id === version.id;
    
    // If clicking on 'to' version, unselect it (keep 'from')
    if (isToVersion) {
      setDiffVersions({ from: diffVersions.from, to: null });
      return;
    }
    
    // If clicking on 'from' version when 'to' is not selected, unselect it
    if (isFromVersion && !diffVersions.to) {
      setDiffVersions({ from: null, to: null });
      return;
    }
    
    // If clicking on 'from' version when 'to' is selected, unselect 'from' and move 'to' to 'from'
    if (isFromVersion && diffVersions.to) {
      setDiffVersions({ from: diffVersions.to, to: null });
      return;
    }
    
    // Normal selection logic
    if (!diffVersions.from) {
      setDiffVersions({ from: version, to: null });
    } else if (!diffVersions.to) {
      // Determine which version is older/newer based on timestamp
      const fromTime = new Date(diffVersions.from.backup_timestamp);
      const toTime = new Date(version.backup_timestamp);

      if (fromTime < toTime) {
        // Current selection is older, new version is newer
        setDiffVersions({ from: diffVersions.from, to: version });
      } else {
        // New version is older, current selection is newer
        setDiffVersions({ from: version, to: diffVersions.from });
      }
    } else {
      // Both are selected and clicking a new version - reset and select new version
      setDiffVersions({ from: version, to: null });
    }
  };

  const clearDiffSelection = () => {
    setDiffVersions({ from: null, to: null });
  };

  const generateDiff = async () => {
    if (!diffVersions.from || !diffVersions.to) return;

    try {
      // Guard: Check if Supabase config is fully loaded before proceeding
      if (!supabaseServiceKey) {
        toast({
          title: "Error",
          description: "Configuration not fully loaded. Please refresh the page.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Use current schema admin client to access storage
      const supabaseAdmin = getSupabaseAdmin();

      // Helper: return cached content or download and cache.
      const getContent = async (path) => {
        const cached = versioningSystem?.getCachedContent(path);
        if (cached) return cached;
        const { data, error } = await supabaseAdmin.storage
          .from(documentBackupBucketName)
          .download(path);
        if (error || !data) throw new Error("Failed to load version for diff");
        const raw = await data.text();
        const parsed = JSON.parse(raw);
        versioningSystem?.setCachedContent(path, parsed);
        return parsed;
      };

      // Load both versions (uses content cache when available).
      const [fromParsed, toParsed] = await Promise.all([
        getContent(diffVersions.from.backup_path),
        getContent(diffVersions.to.backup_path),
      ]);

      // Extract text content from both versions
      const fromText = extractTextFromContent(fromParsed);
      const toText = extractTextFromContent(toParsed);

      // Generate diff
      const diff = generateTextDiff(fromText, toText);

      // Find change positions
      const positions = findChangePositions(diff);

      // Prepare data for diff modal
      const diffData = {
        diffContent: diff,
        fromVersion: {
          ...diffVersions.from,
          versionNumber: getOriginalVersionNumber(diffVersions.from),
          versionName: getVersionDisplayName(diffVersions.from),
          user: diffVersions.from.user_name,
        },
        toVersion: {
          ...diffVersions.to,
          versionNumber: getOriginalVersionNumber(diffVersions.to),
          versionName: getVersionDisplayName(diffVersions.to),
          user: diffVersions.to.user_name,
        },
        changePositions: positions,
      };

      setDiffModalData(diffData);
      setShowDiffModal(true);
    } catch (error) {
      console.error("Error generating diff:", error);
      toast({
        title: "Error",
        description: "Failed to generate diff",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const extractTextFromContent = (content) => {
    if (content.delta && Array.isArray(content.delta.ops)) {
      return content.delta.ops.map((op) => op.insert || "").join("");
    }
    if (content.content && Array.isArray(content.content.ops)) {
      return content.content.ops.map((op) => op.insert || "").join("");
    }
    if (typeof content === "string") {
      return content;
    }
    return "";
  };

  const generateTextDiff = (oldText, newText) => {
    // Use a simple but reliable LCS-based diff like GitHub
    const oldLines = oldText.split("\n");
    const newLines = newText.split("\n");

    // Find longest common subsequence
    const lcs = findLCS(oldLines, newLines);

    const diff = [];
    let oldIndex = 0;
    let newIndex = 0;

    for (const match of lcs) {
      // Add deleted lines before this match
      while (oldIndex < match.oldIndex) {
        diff.push({ type: "removed", content: oldLines[oldIndex] });
        oldIndex++;
      }

      // Add added lines before this match
      while (newIndex < match.newIndex) {
        diff.push({ type: "added", content: newLines[newIndex] });
        newIndex++;
      }

      // Add the matching (unchanged) line
      diff.push({ type: "unchanged", content: match.content });
      newIndex = match.newIndex + 1;
      oldIndex = match.oldIndex + 1;
    }

    // Add remaining deleted lines
    while (oldIndex < oldLines.length) {
      diff.push({ type: "removed", content: oldLines[oldIndex] });
      oldIndex++;
    }

    // Add remaining added lines
    while (newIndex < newLines.length) {
      diff.push({ type: "added", content: newLines[newIndex] });
      newIndex++;
    }

    return diff;
  };

  // Find Longest Common Subsequence
  const findLCS = (a, b) => {
    const m = a.length;
    const n = b.length;

    // Create DP table
    const dp = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0));

    // Build the table
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (a[i - 1] === b[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    // Backtrack to find LCS
    const lcs = [];
    let i = m;
    let j = n;

    while (i > 0 && j > 0) {
      if (a[i - 1] === b[j - 1]) {
        lcs.unshift({
          oldIndex: i - 1,
          newIndex: j - 1,
          content: a[i - 1],
        });
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }

    return lcs;
  };

  // Find all change positions in the diff content
  const findChangePositions = (diffContent) => {
    const positions = [];
    diffContent.forEach((line, index) => {
      if (line.type === "added" || line.type === "removed") {
        positions.push({
          index,
          type: line.type,
          content: line.content,
        });
      }
    });
    return positions;
  };

  // Export diff as JSON (for diff modal)
  const exportDiffAsJSON = () => {
    if (!diffModalData) return;

    const diffJSON = {
      metadata: {
        fromVersion: {
          id: diffModalData.fromVersion.id,
          versionNumber: diffModalData.fromVersion.versionNumber,
          timestamp: diffModalData.fromVersion.backup_timestamp,
          type: diffModalData.fromVersion.backup_type,
          user: diffModalData.fromVersion.user,
        },
        toVersion: {
          id: diffModalData.toVersion.id,
          versionNumber: diffModalData.toVersion.versionNumber,
          timestamp: diffModalData.toVersion.backup_timestamp,
          type: diffModalData.toVersion.backup_type,
          user: diffModalData.toVersion.user,
        },
        generatedAt: new Date().toISOString(),
        documentPath: documentPath,
      },
      changes: {
        added: diffModalData.diffContent
          .filter((line) => line.type === "added")
          .map((line) => line.content),
        removed: diffModalData.diffContent
          .filter((line) => line.type === "removed")
          .map((line) => line.content),
        unchanged: diffModalData.diffContent
          .filter((line) => line.type === "unchanged")
          .map((line) => line.content),
      },
      summary: {
        totalLines: diffModalData.diffContent.length,
        addedLines: diffModalData.diffContent.filter(
          (line) => line.type === "added"
        ).length,
        removedLines: diffModalData.diffContent.filter(
          (line) => line.type === "removed"
        ).length,
        unchangedLines: diffModalData.diffContent.filter(
          (line) => line.type === "unchanged"
        ).length,
      },
      fullDiff: diffModalData.diffContent,
    };

    try {
      const jsonString = JSON.stringify(diffJSON, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `diff_v${
        diffJSON.metadata.fromVersion.versionNumber
      }_to_v${diffJSON.metadata.toVersion.versionNumber}_${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Diff exported as JSON",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error exporting diff:", error);
      toast({
        title: "Error",
        description: "Failed to export diff",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent
        maxH="85vh"
        maxW="90vw"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader flexShrink={0}>
          <VStack align="stretch" spacing={3}>
            <Flex align="center" justify="space-between">
              <Text fontSize="lg" fontWeight="bold">
                Version History
              </Text>
              <Tooltip
                label="Keeps only the 3 most significant auto versions per past day. Manual versions are never affected."
                placement="bottom-end"
                hasArrow
                isOpen={showReduceTooltip}
              >
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="orange"
                  isLoading={isReducing}
                  loadingText="Reducing…"
                  onClick={handleReduceOldVersions}
                  onMouseEnter={() => setShowReduceTooltip(true)}
                  onMouseLeave={() => setShowReduceTooltip(false)}
                  mr={8}
                >
                  Reduce old versions
                </Button>
              </Tooltip>
            </Flex>
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>
                Filter by User
              </FormLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                size="sm"
                maxW="200px"
              >
                <option value="everyone">Everyone</option>
                {availableUsers.map((user) => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" pb={4}>
          {/* Appointment Version Schedule Status */}
          {appointmentVersionInfo && (() => {
            const { preLabel, postLabel, preTargetTime, postTargetTime, preStatus, postStatus } = appointmentVersionInfo;
            const now = Date.now();

            const formatTime = (t) =>
              t instanceof Date
                ? t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : "—";

            // Derive appointment time from post target (postTarget = appt + 60min)
            const apptTime = postTargetTime instanceof Date
              ? new Date(postTargetTime.getTime() - 60 * 60 * 1000)
              : null;

            const formatRemaining = (t) => {
              if (!(t instanceof Date)) return "";
              const ms = t.getTime() - now;
              if (ms <= 0) return "";
              const mins = Math.floor(ms / 60000);
              const secs = Math.floor((ms % 60000) / 1000);
              return mins > 0 ? `in ${mins}m ${secs}s` : `in ${secs}s`;
            };

            const statusColor = (s) =>
              s === "fired" ? "green" : s === "missed" ? "red" : s === "failed" ? "orange" : s === "skipped_no_content" ? "gray" : "blue";

            const statusLabel = (s, targetTime) => {
              if (s === "fired") return "✅ Version created";
              if (s === "missed") return "⚠️ Window passed";
              if (s === "failed") return "❌ Failed to create";
              if (s === "skipped_no_content") return "⚠️ Skipped — doc was empty";
              const rem = formatRemaining(targetTime);
              return rem ? `⏳ Scheduled — fires ${rem}` : "⏳ Scheduled";
            };

            return (
              <Box mb={4} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" fontSize="sm">
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="semibold" color="gray.700">Appointment Auto-Versions</Text>
                  {apptTime && (
                    <Text fontSize="xs" color="gray.500">appt {formatTime(apptTime)}</Text>
                  )}
                </HStack>
                <VStack align="stretch" spacing={1}>
                  <HStack justify="space-between">
                    <HStack spacing={2}>
                      <Badge colorScheme={statusColor(preStatus)}>{preLabel}</Badge>
                      <Text color="gray.500" fontSize="xs">{formatTime(preTargetTime)} (−30min)</Text>
                    </HStack>
                    <Text fontSize="xs" color={`${statusColor(preStatus)}.600`}>
                      {statusLabel(preStatus, preTargetTime)}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <HStack spacing={2}>
                      <Badge colorScheme={statusColor(postStatus)}>{postLabel}</Badge>
                      <Text color="gray.500" fontSize="xs">{formatTime(postTargetTime)} (+1hr)</Text>
                    </HStack>
                    <Text fontSize="xs" color={`${statusColor(postStatus)}.600`}>
                      {statusLabel(postStatus, postTargetTime)}
                    </Text>
                  </HStack>
                </VStack>
                <Text fontSize="xs" color="gray.400" mt={2}>
                  Doc must remain open until trigger time. Reload page to pick up appointment time changes.
                </Text>
              </Box>
            );
          })()}

          {/* Diff Controls */}
          {(diffVersions.from || diffVersions.to) && (
            <Box
              mb={4}
              p={4}
              bg="blue.50"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="bold" color="blue.700">
                    Compare Versions
                  </Text>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearDiffSelection}
                  >
                    Clear Selection
                  </Button>
                </HStack>

                <HStack spacing={4}>
                  <Box flex="1">
                    <Text fontSize="sm" fontWeight="medium" color="blue.600">
                      Older Version:
                    </Text>
                    {diffVersions.from ? (
                      <Text fontSize="sm" color="gray.700">
                        {getVersionDisplayName(diffVersions.from)} -{" "}
                        {formatTimestamp(diffVersions.from.backup_timestamp)}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        Select a version
                      </Text>
                    )}
                  </Box>

                  <Box flex="1">
                    <Text fontSize="sm" fontWeight="medium" color="green.600">
                      Newer Version:
                    </Text>
                    {diffVersions.to ? (
                      <Text fontSize="sm" color="gray.700">
                        {getVersionDisplayName(diffVersions.to)} -{" "}
                        {formatTimestamp(diffVersions.to.backup_timestamp)}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        Select a version
                      </Text>
                    )}
                  </Box>
                </HStack>

                {diffVersions.from && diffVersions.to && (
                  <Button colorScheme="blue" onClick={generateDiff} size="sm">
                    Open Diff View
                  </Button>
                )}
              </VStack>
            </Box>
          )}

          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="lg" />
              <Text mt={4}>Loading versions...</Text>
            </Box>
          ) : error ? (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          ) : filteredVersions.length === 0 ? (
            <Text textAlign="center" py={8} color="gray.500">
              {selectedUser === "everyone"
                ? "No versions found for this document"
                : `No versions found for user: ${selectedUser}`}
            </Text>
          ) : (
            <VStack spacing={4} align="stretch" maxH="40vh" overflowY="auto">
              <Text fontSize="sm" color="gray.600" mb={2}>
                Showing {filteredVersions.length} of {versions.length} versions
                {selectedUser !== "everyone" && ` by ${selectedUser}`}
                {filteredVersions.length > getDailyVersionLimit() && (
                  <Text fontSize="xs" color="blue.600" mt={1}>
                    📝{" "}
                    {isUserBasedVersioningEnabled()
                      ? "Displaying versions based on user count and activity (user-based rules active)"
                      : "Displaying latest version from each user (exceeds normal limit to show all contributors)"}
                  </Text>
                )}
              </Text>

              {Object.entries(getGroupedVersions())
                .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                .map(([date, versionsForDate]) => {
                  const isCollapsed = collapsedDates.has(date);
                  const totalVersionsForDate = getFilteredVersions().filter(
                    (v) => new Date(v.backup_timestamp).toDateString() === date
                  ).length;

                  // Check if we're showing all versions for this date
                  const isShowingAllVersions =
                    versionsForDate.length >= totalVersionsForDate;
                  const hiddenCount = isShowingAllVersions
                    ? 0
                    : totalVersionsForDate - versionsForDate.length;


                  return (
                    <Box
                      key={date}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                    >
                      {/* Date Header */}
                      <HStack
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        cursor="pointer"
                        onClick={() => toggleDateCollapse(date)}
                        _hover={{ bg: "gray.100" }}
                      >
                        <IconButton
                          size="sm"
                          variant="ghost"
                          icon={
                            isCollapsed ? (
                              <ChevronRightIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          aria-label={isCollapsed ? "Expand" : "Collapse"}
                        />
                        <Text fontWeight="bold" fontSize="md">
                          {formatDateForDisplay(date)}
                        </Text>
                        <Badge colorScheme="gray" ml="auto">
                          {versionsForDate.length} version
                          {versionsForDate.length !== 1 ? "s" : ""}
                          {hiddenCount > 0 && ` (${hiddenCount} hidden)`}
                          {isShowingAllVersions &&
                            versionsForDate.length >
                              (date === new Date().toDateString()
                                ? getDailyVersionLimit()
                                : getEndOfDayVersionLimit()) &&
                            ` (${
                              showAllVersionsForDate.has(date)
                                ? "all versions shown"
                                : isUserBasedVersioningEnabled()
                                ? "user-based rules"
                                : "all contributors shown"
                            })`}
                        </Badge>
                        {hiddenCount > 0 && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleShowAllVersions(date);
                            }}
                            ml={2}
                          >
                            {showAllVersionsForDate.has(date)
                              ? "Show Filtered"
                              : "View All"}
                          </Button>
                        )}
                        {showAllVersionsForDate.has(date) && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleShowAllVersions(date);
                            }}
                            ml={2}
                          >
                            Revert
                          </Button>
                        )}
                      </HStack>

                      {/* Versions for this date */}
                      <Collapse in={!isCollapsed}>
                        <VStack spacing={2} p={2} align="stretch">
                          {versionsForDate.map((version) => {
                            const isFirstSelected = diffVersions.from?.id === version.id && !diffVersions.to;
                            const isSelectedForDiff = diffVersions.from?.id === version.id || diffVersions.to?.id === version.id;
                            
                            return (
                            <Box
                              key={version.id}
                              p={3}
                              border="1px solid"
                              borderColor={isFirstSelected ? "gray.300" : "gray.200"}
                              borderRadius="md"
                              cursor="pointer"
                              bg={
                                selectedVersion?.id === version.id
                                  ? "blue.50"
                                  : isFirstSelected
                                  ? "gray.100"
                                  : "white"
                              }
                              opacity={isFirstSelected ? 0.6 : 1}
                              _hover={{ bg: isFirstSelected ? "gray.100" : "gray.50" }}
                              onClick={() => loadVersionContent(version)}
                            >
                              <HStack justify="space-between" mb={2}>
                                <Text fontWeight="bold">
                                  {getVersionDisplayName(version)}
                                </Text>
                                <Badge
                                  colorScheme={getVersionTypeColor(
                                    version.backup_type
                                  )}
                                >
                                  {version.backup_type}
                                </Badge>
                              </HStack>

                              <Text fontSize="sm" color="gray.600" mb={2}>
                                {formatTimestamp(version.backup_timestamp)}
                              </Text>

                              {version.backup_type === "manual" &&
                              version.user_name &&
                              version.user_name.includes(" - ") ? (
                                <Text
                                  fontSize="sm"
                                  color="blue.600"
                                  fontWeight="medium"
                                  mb={1}
                                >
                                  📝 "{version.user_name.split(" - ")[1]}"
                                </Text>
                              ) : version.backup_type === "manual" &&
                                !version.user_name ? (
                                <Text
                                  fontSize="sm"
                                  color="blue.600"
                                  fontWeight="medium"
                                  mb={1}
                                >
                                  📝 Manual version (user unknown)
                                </Text>
                              ) : null}

                              {version.user_name ? (
                                <Text fontSize="sm" color="gray.500">
                                  {version.user_name.includes(" & ")
                                    ? `Edited by: ${version.user_name}`
                                    : version.user_name.includes(" + ")
                                    ? `Edited by: ${version.user_name}`
                                    : `Edited by: ${
                                        version.backup_type === "manual" &&
                                        version.user_name.includes(" - ")
                                          ? version.user_name.split(" - ")[0]
                                          : version.user_name
                                      }`}
                                </Text>
                              ) : (
                                <Text
                                  fontSize="sm"
                                  color="gray.400"
                                  fontStyle="italic"
                                >
                                  User information not available
                                </Text>
                              )}

                              {version.file_size && (
                                <Text fontSize="sm" color="gray.500">
                                  Size: {(version.file_size / 1024).toFixed(1)}{" "}
                                  KB
                                </Text>
                              )}

                              {/* Diff buttons */}
                              <HStack mt={2} spacing={1} justify="center">
                                <IconButton
                                  size="sm"
                                  variant={
                                    diffVersions.from?.id === version.id ||
                                    diffVersions.to?.id === version.id
                                      ? "solid"
                                      : "outline"
                                  }
                                  colorScheme={
                                    diffVersions.from?.id === version.id
                                      ? "blue"
                                      : diffVersions.to?.id === version.id
                                      ? "green"
                                      : "gray"
                                  }
                                  icon={
                                    diffVersions.from?.id === version.id ? (
                                      <ArrowLeftIcon />
                                    ) : diffVersions.to?.id === version.id ? (
                                      <ArrowRightIcon />
                                    ) : (
                                      <ArrowRightIcon />
                                    )
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDiffSelection(version);
                                  }}
                                  aria-label={
                                    diffVersions.from?.id === version.id && !diffVersions.to
                                      ? "Selected as older version (click to unselect)"
                                      : diffVersions.from?.id === version.id
                                      ? "Selected as older version"
                                      : diffVersions.to?.id === version.id
                                      ? "Selected as newer version (click to unselect)"
                                      : "Select for comparison"
                                  }
                                  title={
                                    diffVersions.from?.id === version.id && !diffVersions.to
                                      ? "Older version (from) - Click again to unselect"
                                      : diffVersions.from?.id === version.id
                                      ? "Older version (from)"
                                      : diffVersions.to?.id === version.id
                                      ? "Newer version (to) - Click again to unselect"
                                      : "Click to compare with this version"
                                  }
                                />
                              </HStack>
                            </Box>
                            );
                          })}
                        </VStack>
                      </Collapse>
                    </Box>
                  );
                })}
            </VStack>
          )}

          {selectedVersion && (
            <Box mt={6} p={4} bg="gray.50" borderRadius="md">
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="bold">Preview</Text>
                <Text fontSize="sm" color="gray.500">
                  {getVersionDisplayName(selectedVersion)}
                </Text>
              </HStack>
              {loadingPreview ? (
                <Box textAlign="center" py={4}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Loading preview...
                  </Text>
                </Box>
              ) : previewContent ? (
                <Box
                  maxH="150px"
                  overflowY="auto"
                  p={4}
                  bg="white"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                >
                  <Text
                    fontSize="sm"
                    color="gray.700"
                    whiteSpace="pre-wrap"
                    lineHeight="1.5"
                    fontFamily="system-ui, -apple-system, sans-serif"
                  >
                    {getContentPreview(previewContent)}
                  </Text>
                  {getContentPreview(previewContent).length >= 300 && (
                    <Text
                      fontSize="xs"
                      color="gray.400"
                      mt={2}
                      textAlign="right"
                    >
                      ... (truncated)
                    </Text>
                  )}
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Text fontSize="sm" color="gray.500">
                    No preview available
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter
          flexShrink={0}
          borderTop="1px solid"
          borderColor="gray.200"
        >
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
          {selectedVersion && (
            <Tooltip
              label={
                documentReadOnly
                  ? "Restore is disabled while the note is in read-only mode"
                  : "Replace the current document with this version"
              }
              hasArrow
            >
              <Button
                colorScheme="blue"
                onClick={restoreVersion}
                isDisabled={documentReadOnly}
              >
                Restore Version
              </Button>
            </Tooltip>
          )}
        </ModalFooter>
      </ModalContent>

      {/* Diff Modal */}
      <DiffModal
        isOpen={showDiffModal}
        onClose={() => setShowDiffModal(false)}
        diffContent={diffModalData?.diffContent}
        fromVersion={diffModalData?.fromVersion}
        toVersion={diffModalData?.toVersion}
        changePositions={diffModalData?.changePositions || []}
        onExportDiff={exportDiffAsJSON}
        isAdmin={isAdmin()}
      />
    </Modal>
  );
};

export default VersionHistoryModal;
